// Mirror of figano-backend/src/constants/specification-types.ts — keep the
// two files identical (only getSpecMeta/isValidSpecType accept plain strings
// here, because the admin reads the type from the URL).

/**
 * Specification type registry — single source of truth for every
 * specification dictionary (AttributeDictionary discriminator) across the
 * project. Each type is its own Mongoose model + MongoDB collection and can
 * back a dynamic dropdown on an AccountType (product type) field via
 * `dynamicSource.collectionName = <modelName>`.
 *
 * Adding a new module = add one entry here + seed rows. No new model,
 * service, controller, route or admin page required.
 *
 * `parentType` declares that documents of this type may reference a parent
 * doc of that type (hierarchical dictionaries). No cosmetics type needs a
 * hierarchy today; the mechanism stays available.
 *
 * `legacy: true` marks dictionaries left over from the old electronics
 * catalog. They are NOT part of the CasmaShop cosmetics catalog and are kept
 * only because existing AccountType fields still point at their collections
 * (removing the model would break those dropdowns). They stay registered
 * (model + collection whitelist) but are excluded from
 * `ACTIVE_SPECIFICATION_TYPES` / `ALL_SPEC_TYPES` and grouped under
 * «قدیمی». Drop an entry once no AccountType references it anymore.
 */

export type ActiveSpecificationType =
  // Skin & hair
  | 'skin-type'
  | 'skin-concern'
  | 'hair-type'
  | 'hair-concern'
  // Formula & ingredients
  | 'key-ingredient'
  | 'texture'
  | 'free-from'
  | 'certification'
  // Makeup & color
  | 'color-family'
  | 'finish'
  | 'coverage'
  // Fragrance
  | 'fragrance-family'
  | 'fragrance-note'
  | 'fragrance-concentration'
  // General
  | 'product-type'
  | 'product-brand'
  | 'country-of-origin'
  | 'package-type'
  | 'target-audience';

/** Old electronics dictionaries still referenced by existing AccountTypes. */
export type LegacySpecificationType =
  | 'product-color'
  | 'product-condition'
  | 'warranty-type'
  | 'warranty-duration'
  | 'storage-type'
  | 'storage-brand'
  | 'storage-capacity'
  | 'battery-capacity'
  | 'charging-power'
  | 'charging-technology'
  | 'camera-resolution'
  | 'camera-feature'
  | 'bluetooth-version'
  | 'connectivity-feature'
  | 'port'
  | 'usb-version'
  | 'audio-feature'
  | 'body-material';

export type SpecificationType = ActiveSpecificationType | LegacySpecificationType;

export type SpecificationGroup =
  | 'skin-hair'
  | 'formula'
  | 'makeup'
  | 'fragrance'
  | 'general'
  | 'legacy';

export interface SpecificationTypeMeta {
  type: SpecificationType;
  /** Persian label shown in admin sidebar and page headers. */
  labelFa: string;
  /** English label / column header. */
  labelEn: string;
  /** Logical grouping for sidebar organization. */
  group: SpecificationGroup;
  /** Parent type for hierarchical specs. */
  parentType?: SpecificationType;
  /** Optional hint shown on the admin page. */
  hint?: string;
  /** Leftover from the old catalog — registered for data compatibility only. */
  legacy?: boolean;
  /**
   * Mongoose model name registered for this spec type. Used as the `ref` on
   * parent references and as the `collectionName` AccountType dynamic
   * dropdowns target. Derived from `type` if not overridden.
   */
  modelName: string;
  /**
   * MongoDB collection name (lowercased plural). What AccountType's
   * `dynamicSource.collectionName` should point at.
   */
  collectionName: string;
}

export const SPECIFICATION_GROUPS: Record<SpecificationGroup, { labelFa: string; order: number }> = {
  'skin-hair': { labelFa: 'پوست و مو', order: 1 },
  formula: { labelFa: 'فرمولاسیون و ترکیبات', order: 2 },
  makeup: { labelFa: 'آرایش و رنگ', order: 3 },
  fragrance: { labelFa: 'عطر و رایحه', order: 4 },
  general: { labelFa: 'عمومی محصول', order: 5 },
  legacy: { labelFa: 'قدیمی (خارج از کاتالوگ)', order: 99 },
};

// Convert kebab-case type → PascalCase model name (e.g. 'skin-type' → 'SkinType').
const toPascal = (s: string): string =>
  s
    .split(/[-_]/)
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
    .join('');

// Convert kebab-case → snake_case collection (e.g. 'skin-type' → 'skin_types').
const toCollection = (s: string): string => s.replace(/-/g, '_') + 's';

const enrich = (
  m: Omit<SpecificationTypeMeta, 'modelName' | 'collectionName'>
): SpecificationTypeMeta => ({
  ...m,
  modelName: toPascal(m.type),
  collectionName: toCollection(m.type),
});

type RawSpecMeta = Omit<SpecificationTypeMeta, 'modelName' | 'collectionName'>;

const LEGACY_HINT =
  'از کاتالوگ قدیمی؛ فقط برای سازگاری با انواع محصول موجود نگه داشته شده و در کاتالوگ آرایشی‌بهداشتی استفاده نمی‌شود.';

const legacy = (type: LegacySpecificationType, labelFa: string, labelEn: string): RawSpecMeta => ({
  type,
  labelFa: `${labelFa} (قدیمی)`,
  labelEn: `${labelEn} (legacy)`,
  group: 'legacy',
  legacy: true,
  hint: LEGACY_HINT,
});

const RAW_SPECIFICATION_TYPES: RawSpecMeta[] = [
  // Skin & hair
  { type: 'skin-type', labelFa: 'نوع پوست', labelEn: 'Skin Types', group: 'skin-hair' },
  { type: 'skin-concern', labelFa: 'نگرانی پوستی', labelEn: 'Skin Concerns', group: 'skin-hair' },
  { type: 'hair-type', labelFa: 'نوع مو', labelEn: 'Hair Types', group: 'skin-hair' },
  { type: 'hair-concern', labelFa: 'مشکلات مو و کف سر', labelEn: 'Hair & Scalp Concerns', group: 'skin-hair' },
  // Formula & ingredients
  { type: 'key-ingredient', labelFa: 'ترکیب کلیدی', labelEn: 'Key Ingredients', group: 'formula' },
  { type: 'texture', labelFa: 'بافت', labelEn: 'Textures', group: 'formula' },
  { type: 'free-from', labelFa: 'فاقد ترکیبات', labelEn: 'Free-From Claims', group: 'formula' },
  { type: 'certification', labelFa: 'گواهی‌ها', labelEn: 'Certifications', group: 'formula' },
  // Makeup & color
  { type: 'color-family', labelFa: 'خانواده رنگ', labelEn: 'Color Families', group: 'makeup' },
  { type: 'finish', labelFa: 'فینیش', labelEn: 'Finishes', group: 'makeup' },
  { type: 'coverage', labelFa: 'میزان پوشانندگی', labelEn: 'Coverage Levels', group: 'makeup' },
  // Fragrance
  { type: 'fragrance-family', labelFa: 'گروه بویایی', labelEn: 'Fragrance Families', group: 'fragrance' },
  { type: 'fragrance-note', labelFa: 'نت عطر', labelEn: 'Fragrance Notes', group: 'fragrance' },
  { type: 'fragrance-concentration', labelFa: 'غلظت عطر', labelEn: 'Fragrance Concentrations', group: 'fragrance' },
  // General
  { type: 'product-type', labelFa: 'نوع محصول', labelEn: 'Product Types', group: 'general' },
  { type: 'product-brand', labelFa: 'برند محصول', labelEn: 'Product Brands', group: 'general' },
  { type: 'country-of-origin', labelFa: 'کشور سازنده', labelEn: 'Countries of Origin', group: 'general' },
  { type: 'package-type', labelFa: 'نوع بسته‌بندی', labelEn: 'Package Types', group: 'general' },
  { type: 'target-audience', labelFa: 'مناسب برای', labelEn: 'Target Audiences', group: 'general' },

  // Legacy (old electronics catalog) — see file header.
  legacy('product-color', 'رنگ محصول', 'Product Colors'),
  legacy('product-condition', 'وضعیت کالا', 'Product Conditions'),
  legacy('warranty-type', 'نوع گارانتی', 'Warranty Types'),
  legacy('warranty-duration', 'مدت گارانتی', 'Warranty Durations'),
  legacy('storage-type', 'نوع حافظه', 'Storage Types'),
  legacy('storage-brand', 'برند حافظه', 'Storage Brands'),
  legacy('storage-capacity', 'ظرفیت حافظه', 'Storage Capacities'),
  legacy('battery-capacity', 'ظرفیت باتری', 'Battery Capacities'),
  legacy('charging-power', 'توان شارژ', 'Charging Powers'),
  legacy('charging-technology', 'فناوری شارژ', 'Charging Technologies'),
  legacy('camera-resolution', 'رزولوشن دوربین', 'Camera Resolutions'),
  legacy('camera-feature', 'قابلیت‌های دوربین', 'Camera Features'),
  legacy('bluetooth-version', 'نسخه بلوتوث', 'Bluetooth Versions'),
  legacy('connectivity-feature', 'ارتباطات نزدیک', 'Connectivity Features'),
  legacy('port', 'پورت‌ها', 'Ports'),
  legacy('usb-version', 'نسخه USB', 'USB Versions'),
  legacy('audio-feature', 'صدا', 'Audio Features'),
  legacy('body-material', 'جنس بدنه', 'Body Materials'),
];

/** Every registered type (active + legacy) — models, collection whitelist, admin CRUD. */
export const SPECIFICATION_TYPES: SpecificationTypeMeta[] = RAW_SPECIFICATION_TYPES.map(enrich);

/** The cosmetics catalog proper — what menus and pickers should offer. */
export const ACTIVE_SPECIFICATION_TYPES: SpecificationTypeMeta[] = SPECIFICATION_TYPES.filter(
  (m) => !m.legacy
);

const TYPE_INDEX = new Map<SpecificationType, SpecificationTypeMeta>(
  SPECIFICATION_TYPES.map((m) => [m.type, m])
);

/** Active (non-legacy) type keys — used for bulk public listings. */
export const ALL_SPEC_TYPES: SpecificationType[] = ACTIVE_SPECIFICATION_TYPES.map((m) => m.type);

export const getSpecMeta = (type: string): SpecificationTypeMeta | undefined =>
  TYPE_INDEX.get(type as SpecificationType);

/** True for any registered type (legacy included, so old rows stay manageable). */
export const isValidSpecType = (s: string): s is SpecificationType => TYPE_INDEX.has(s as SpecificationType);

export const isLegacySpecType = (s: string): boolean =>
  !!TYPE_INDEX.get(s as SpecificationType)?.legacy;
